<template>
  <div>
    <div
      class="mb-2"
    >
      <h3
        class="mb-2"
      >
        Unit Customisation
      </h3>
      <p
        class="mb-0"
      >
        <small>
          You are currently customising the unit: <br>
          <h6 class="small">
            <strong>{{ activeCreativeTemplateName }}</strong>
          </h6>
        </small>
      </p>
    </div>
    <b-button
      variant="secondary"
      size="sm"
      class="mt-1 mb-5"
      data-test="template-properties-reset-btn"
      @click="reset"
      :disabled="!propertiesCanBeUpdated"
    >
      Reset
    </b-button>
    <section
      class="card p-3 mb-2"
      v-if="displayBackgroundColour"
    >
      <h5
        class="mb-4"
      >
        <i class="fas fa-swatchbook text-cyan mr-2" /> Layout
      </h5>
      <div>
        <div
          class="mb-4"
          v-if="propertyNames.indexOf('layoutStyle') !== -1"
        >
          <p
            class="mb-0"
          >
            Layout
          </p>
          <layout-group
            :disabled="!propertiesCanBeUpdated"
            :creative-size="activeCreativeTemplateSize"
            :value="Number(properties['layoutStyle'])"
            :key="activeCreativeTemplateSize"
            @updateValue="updateProperty('layoutStyle', $event)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
        >
          <p
            class="mb-0 form-label"
          >
            Unit Background
          </p>
          <base-colour-select
            class="col-6"
            :disabled="!propertiesCanBeUpdated"
            :key="properties.backgroundColour"
            :value="properties.backgroundColour"
            @updateValue="updateProperty('backgroundColour', $event)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
          v-if="propertyNames.indexOf('borderColour') !== -1"
        >
          <p
            class="mb-0 form-label"
          >
            Border Colour
          </p>
          <base-colour-select
            class="col-6"
            :disabled="!propertiesCanBeUpdated"
            :key="properties.borderColour"
            :value="properties.borderColour"
            @updateValue="updateProperty( 'borderColour', $event)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
          v-if="propertyNames.indexOf('borderWidth') !== -1"
        >
          <p
            class="mb-0 form-label"
          >
            Border Width
          </p>
          <base-counter
            class="col-5"
            unit="px"
            :disabled="!propertiesCanBeUpdated"
            :key="properties.borderWidth"
            :value="properties.borderWidth"
            @updateValue="updateProperty( 'borderWidth', $event)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
          />
        </div>
        <div v-if="displayMarginProperties">
          <p
            class="mb-2 form-label"
          >
            Unit Spacing
          </p>
          <margin-group
            :key="`${properties.marginLeft}${properties.marginRight}${properties.marginTop}${properties.marginBottom}`"
            :left="Number(properties['marginLeft'])"
            :right="Number(properties['marginRight'])"
            :top="Number(properties['marginTop'])"
            :bottom="Number(properties['marginBottom'])"
            :linked="properties['marginsAreLinked'] === 'true'"
            :disabled="!propertiesCanBeUpdated"
            @updateValues="updateProperties"
            @updateValue="updateProperty('marginsAreLinked', $event)"
          />
        </div>
      </div>
    </section>
    <section
      class="card p-3 mb-2"
      v-if="creativeHasImageProperties"
    >
      <h5
        class="mb-4"
      >
        <i class="fas fa-image text-pink mr-2" /> Image
      </h5>
      <div>
        <div
          v-if="displayImagePlacement"
          class="mb-4"
        >
          <p
            class="mb-2 form-label"
          >
            Image Spacing
          </p>
          <image-margin-group
            :key="`${properties.imageMarginLeft}${properties.imageMarginRight}${properties.imageMarginTop}${properties.imageMarginBottom}`"
            :left="Number(properties['imageMarginLeft'])"
            :right="Number(properties['imageMarginRight'])"
            :top="Number(properties['imageMarginTop'])"
            :bottom="Number(properties['imageMarginBottom'])"
            :linked="properties['imageMarginsAreLinked'] === 'true'"
            :disabled="!propertiesCanBeUpdated"
            @updateValues="updateProperties"
            @updateValue="updateProperty('imageMarginsAreLinked', $event)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
          v-if="displayImagePlacement"
        >
          <p
            class="mb-0 form-label"
          >
            Image Placement
          </p>
          <image-placement
            :key="properties.imagePlacement"
            :disabled="!propertiesCanBeUpdated"
            :value="properties.imagePlacement"
            :is-horizontal-placement="activeCreativeTemplateName === 'Billboard (970x250)'"
            @updateValue="updateProperty('imagePlacement', $event)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-3"
        >
          <p
            class="mb-0 form-label"
          >
            {{ imagePropertyName }}
          </p>
          <base-counter
            class="col-5"
            unit="px"
            :step="5"
            :key="properties[`image${imagePropertyName}`]"
            :max="imagePropertyName === 'Width' ? 500 : 300"
            :disabled="!propertiesCanBeUpdated"
            :value="properties[`image${imagePropertyName}`]"
            @updateValue="updateProperty(`image${imagePropertyName}`, $event)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center"
          v-if="propertyNames.indexOf('imageBorderRadius') !== -1"
        >
          <p
            class="mb-0 form-label"
          >
            Corner Rounding
          </p>
          <base-slider
            :step="1"
            :min="0"
            :max="75"
            :key="properties.imageBorderRadius"
            :disabled="!propertiesCanBeUpdated"
            :value="Number(properties.imageBorderRadius)"
            @updateValue="updateProperty('imageBorderRadius', $event)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
            class="col-6"
          />
        </div>
      </div>
      <!-- <hr class="my-5"> -->
    </section>
    <section
      class="card p-3 mb-2"
      v-if="displayHeadlineProperties"
    >
      <h5
        class="mb-4"
      >
        <i class="fas fa-header text-green mr-2" /> Headline
      </h5>
      <div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
          v-if="propertyNames.indexOf('headlineFontSize') !== -1"
        >
          <p
            class="mb-0 form-label"
          >
            Font Size
          </p>
          <base-counter
            class="col-5"
            unit="px"
            :min="18"
            :max="35"
            :key="properties.headlineFontSize"
            :disabled="!propertiesCanBeUpdated"
            :value="Number(properties.headlineFontSize)"
            @updateValue="updateProperty('headlineFontSize', $event)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
          v-if="displayV4Properties"
        >
          <p
            class="mb-0 form-label"
          >
            Font Weight
          </p>
          <base-font-weight-select
            class="col-6"
            :value="Number(properties.headlineFontWeight)"
            :disabled="!propertiesCanBeUpdated"
            @updateValue="updateProperty('headlineFontWeight', $event)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
          v-if="propertyNames.indexOf('headlineFontAlign') !== -1"
        >
          <p
            class="mb-0 form-label"
          >
            Font Align
          </p>
          <align-group
            :key="properties.headlineFontAlign"
            :disabled="!propertiesCanBeUpdated"
            :value="properties.headlineFontAlign"
            @updateValue="updateProperty('headlineFontAlign', $event)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center"
          v-if="propertyNames.indexOf('headlineFontColour') !== -1"
        >
          <p
            class="mb-0 form-label"
          >
            Font Colour
          </p>
          <base-colour-select
            class="col-6"
            :key="properties.headlineFontColour"
            :disabled="!propertiesCanBeUpdated"
            :value="properties.headlineFontColour"
            @updateValue="updateProperty( 'headlineFontColour', $event)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
          />
        </div>
      </div>
      <!-- <hr class="my-5"> -->
    </section>
    <section
      class="card p-3 mb-2"
      v-if="displayBodyProperties"
    >
      <h5
        class="mb-4"
      >
        <i class="fas fa-text-size text-indigo mr-2" /> Body Copy
      </h5>
      <div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
          v-if="propertyNames.indexOf('bodyFontSize') !== -1"
        >
          <p
            class="mb-0 form-label"
          >
            Font Size
          </p>
          <base-counter
            class="col-5"
            unit="px"
            :min="12"
            :max="20"
            :key="properties.bodyFontSize"
            :disabled="!propertiesCanBeUpdated"
            :value="properties.bodyFontSize"
            @updateValue="updateProperty( 'bodyFontSize', $event)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
          v-if="propertyNames.indexOf('bodyFontColour') !== -1"
        >
          <p
            class="mb-0 form-label"
          >
            Font Colour
          </p>
          <base-colour-select
            class="col-6"
            :key="properties.bodyFontColour"
            :disabled="!propertiesCanBeUpdated"
            :value="properties.bodyFontColour"
            @updateValue="updateProperty('bodyFontColour', $event)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
          v-if="displayV4Properties"
        >
          <p
            class="mb-0 form-label"
          >
            Font Weight
          </p>
          <base-font-weight-select
            class="col-6"
            :value="Number(properties.bodyFontWeight)"
            :disabled="!propertiesCanBeUpdated"
            @updateValue="updateProperty('bodyFontWeight', $event)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
          v-if="displayV4Properties"
        >
          <p
            class="mb-0 form-label"
          >
            Text Length
          </p>
          <base-slider
            :step="1"
            :min="0"
            :max="10"
            :key="properties.bodyClamp"
            :disabled="!propertiesCanBeUpdated"
            :value="Number(properties.bodyClamp)"
            @updateValue="updateProperty('bodyClamp', $event)"
            class="col-6"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center"
          v-if="propertyNames.indexOf('bodyFontAlign') !== -1"
        >
          <p
            class="mb-0 form-label"
          >
            Text Align
          </p>
          <align-group
            :key="properties.bodyFontAlign"
            :disabled="!propertiesCanBeUpdated"
            :value="properties.bodyFontAlign"
            @updateValue="updateProperty('bodyFontAlign', $event)"
          />
        </div>
      </div>
      <!-- <hr class="my-5"> -->
    </section>
    <section
      class="card p-3 mb-2"
      v-if="displayCtaProperties"
    >
      <h5
        class="mb-4"
      >
        <i class="fas fa-arrow-pointer text-orange mr-2" /> Call to Action
      </h5>
      <div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
          v-if="propertyNames.indexOf('ctaFontColour') !== -1"
        >
          <p
            class="mb-0 form-label"
          >
            Font Colour
          </p>
          <base-colour-select
            class="col-6"
            :key="properties.ctaFontColour"
            :disabled="!propertiesCanBeUpdated"
            :value="properties.ctaFontColour"
            @updateValue="updateProperty('ctaFontColour', $event)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
          v-if="propertyNames.indexOf('ctaBackgroundColour') !== -1"
        >
          <p
            class="mb-0 form-label"
          >
            Cta Background
          </p>
          <base-colour-select
            class="col-6"
            :key="properties.ctaBackgroundColour"
            :disabled="!propertiesCanBeUpdated"
            :value="properties.ctaBackgroundColour"
            @updateValue="updateProperty('ctaBackgroundColour', $event)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
          v-if="propertyNames.indexOf('ctaBorderColour') !== -1"
        >
          <p
            class="mb-0 form-label"
          >
            Cta Border
          </p>
          <base-colour-select
            class="col-6"
            :key="properties.ctaBorderColour"
            :disabled="!propertiesCanBeUpdated"
            :value="properties.ctaBorderColour"
            @updateValue="updateProperty('ctBorderColour', $event)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
          v-if="propertyNames.indexOf('ctaFontSize') !== -1"
        >
          <p
            class="mb-0 form-label"
          >
            Font Size
          </p>
          <base-counter
            class="col-5"
            unit="px"
            :min="12"
            :max="20"
            :key="properties.ctaFontSize"
            :disabled="!propertiesCanBeUpdated"
            :value="properties.ctaFontSize"
            @updateValue="updateProperty('ctaFontSize', $event)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center mb-4"
          v-if="propertyNames.indexOf('ctaPadding') !== -1"
        >
          <p
            class="mb-0 form-label"
          >
            Button Size
          </p>
          <base-slider
            :step="1"
            :min="0"
            :max="15"
            :key="properties.ctaPadding"
            :disabled="!propertiesCanBeUpdated"
            :value="Number(properties.ctaPadding)"
            @updateValue="updateProperty('ctaPadding', $event)"
            class="col-6"
          />
        </div>
        <div
          class="row gutters-0 justify-content-between align-items-center"
          v-if="propertyNames.indexOf('ctaBorderRadius') !== -1"
        >
          <p
            class="mb-0 form-label"
          >
            Corner Rounding
          </p>
          <base-slider
            :step="1"
            :min="1"
            :max="25"
            :key="properties.ctaBorderRadius"
            :disabled="!propertiesCanBeUpdated"
            :value="Number(properties.ctaBorderRadius)"
            @updateValue="updateProperty('ctaBorderRadius', $event)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
            class="col-6"
          />
        </div>
      </div>
      <!-- <hr class="my-5"> -->
    </section>
    <section
      class="card p-3 mb-5"
      v-if="displayLogoProperties"
    >
      <h5
        class="mb-3"
      >
        <i class="fas fa-globe text-purple mr-2" /> Logo
      </h5>
      <logo-upload
        logo-type="unit"
        :disabled="!propertiesCanBeUpdated"
        :key="selectedCreativeId"
      />
      <div
        class="row gutters-0 justify-content-between align-items-center"
        v-if="displayLogoPlacement"
      >
        <p
          class="mb-0 form-label"
        >
          Logo Placement
        </p>
        <logo-placement
          :key="properties.logoPlacement"
          :disabled="!propertiesCanBeUpdated"
          :value="properties.logoPlacement"
          @updateValue="updateProperty('logoPlacement', $event)"
        />
      </div>
      <div
        class="row gutters-0 justify-content-between align-items-center my-5"
        v-if="propertyNames.indexOf('logoSize') !== -1"
      >
        <p
          class="mb-0 form-label"
        >
          Logo Size
        </p>
        <base-slider
          :min="10"
          :max="100"
          :key="properties.logoSize"
          :disabled="!propertiesCanBeUpdated"
          :value="Number(properties.logoSize)"
          @updateValue="updateProperty('logoSize', $event)"
          :step="2"
          class="col-6"
        />
      </div>
      <div
        class="row gutters-0 justify-content-between align-items-center mb-4"
        v-if="displayLogoSpacingProperties"
      >
        <p
          class="mb-3 form-label"
        >
          Logo Spacing
        </p>
        <logo-margin-group
          :key="`${properties.logoMarginLeft}${properties.logoMarginRight}${properties.logoMarginTop}${properties.logoMarginBottom}`"
          :left="Number(properties['logoMarginLeft'])"
          :right="Number(properties['logoMarginRight'])"
          :top="Number(properties['logoMarginTop'])"
          :bottom="Number(properties['logoMarginBottom'])"
          :linked="properties['logoMarginsAreLinked'] === 'true'"
          :disabled="!propertiesCanBeUpdated"
          @updateValues="updateProperties"
          @updateValue="updateProperty('logoMarginsAreLinked', $event)"
        />
      </div>
    </section>
  </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';
import * as templatesTypes from '@/modules/templates/store/types';

import LogoUpload from '@/modules/content/components/CreativesPage/Sidebar/LogoUpload';
import LogoPlacement from '@/modules/content/components/CreativesPage/Sidebar/LogoPlacement';
import BaseColourSelect from '@/components/BaseColourSelect';
import BaseCounter from '@/components/BaseCounter';
import BaseSlider from '@/components/BaseSlider.vue';
import BaseFontWeightSelect from '@/components/BaseFontWeightSelect.vue';
import AlignGroup from '@/modules/content/components/CreativesPage/Sidebar/AlignGroup.vue';
import ImagePlacement from '@/modules/content/components/CreativesPage/Sidebar/ImagePlacement.vue';
import MarginGroup from '@/modules/content/components/CreativesPage/Sidebar/MarginGroup.vue';
import ImageMarginGroup from '@/modules/content/components/CreativesPage/Sidebar/ImageMarginGroup.vue';
import LogoMarginGroup from '@/modules/content/components/CreativesPage/Sidebar/LogoMarginGroup.vue';
import LayoutGroup from '@/modules/content/components/CreativesPage/Sidebar/LayoutGroup.vue';

export default {
  name: 'UnitCreativeProperties',
  components: {
    LayoutGroup,
    MarginGroup,
    ImageMarginGroup,
    ImagePlacement,
    AlignGroup,
    LogoUpload,
    LogoPlacement,
    LogoMarginGroup,
    BaseSlider,
    BaseColourSelect,
    BaseCounter,
    BaseFontWeightSelect,
  },
  data() {
    return {
      delayedUpdates: [],
      isUpdateInProgress: false,
      areMarginsLinked: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'order',
      'selectedAdvertiser',
    ]),
    ...mapState('content', [
      'isSidebarInputFocused',
      'selectedCreativeId',
      'feedId',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_SELF_SERVICE_CAMPAIGN_ACCESSED_BY_ADMIN,
      campaignTypes.GET_IS_SELF_SERVICE_CAMPAIGN,
      campaignTypes.GET_IS_CAMPAIGN_DRAFT,
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_CURRENT_SELECTED_CREATIVE,
      contentTypes.GET_ACTIVE_CREATIVES,
      contentTypes.GET_ACTIVE_CREATIVE_TEMPLATES,
      contentTypes.GET_TEMPLATE_NAME_BY_ID,
    ]),
    ...mapGetters('templates', [
      templatesTypes.GET_TEMPLATE_EDITABLE_PROPERTIES,
    ]),
    activeCreative() {
      return this.GET_ACTIVE_CREATIVES.find((creative) => creative.id === this.selectedCreativeId);
    },
    properties() {
      return this.activeCreative.properties;
    },
    propertyNames() {
      return [...new Set(Object.keys(this.properties))];
    },
    activeCreativeTemplate() {
      const currentTemplate = this.GET_ACTIVE_CREATIVE_TEMPLATES.find((template) => template.creativeId === this.selectedCreativeId);
      return currentTemplate || this.GET_ACTIVE_CREATIVE_TEMPLATES[0];
    },
    activeCreativeTemplateSize() {
      return `${this.activeCreativeTemplate.width}x${this.activeCreativeTemplate.height}`;
    },
    activeCreativeTemplateName() {
      return this.activeCreativeTemplate.name;
    },
    creativeHasImageProperties() {
      return Object.keys(this.properties).some((property) => property.startsWith('image'));
    },
    imagePropertyName() {
      if (Object.keys(this.properties).find((property) => property === 'imageWidth')) return 'Width';
      if (Object.keys(this.properties).find((property) => property === 'imageHeight')) return 'Height';
      return '';
    },
    propertiesCanBeUpdated() {
      return !this.GET_IS_CAMPAIGN_READONLY;
    },
    displayV4Properties() {
      return this.propertyNames.some((property) => property.startsWith('bodyClamp'));
    },
    displayBackgroundColour() {
      return this.propertyNames.indexOf('backgroundColour') !== -1;
    },
    displayMarginProperties() {
      return this.propertyNames.some((property) => property.startsWith('margin'));
    },
    displayImagePlacement() {
      return this.propertyNames.some((property) => property.startsWith('imagePlacement'));
    },
    displayImageMarginProperties() {
      return this.propertyNames.some((property) => property.startsWith('imageMargin'));
    },
    displayHeadlineProperties() {
      return this.propertyNames.some((property) => property.startsWith('headline'));
    },
    displayBodyProperties() {
      return this.propertyNames.some((property) => property.startsWith('body'));
    },
    displayCtaProperties() {
      return this.propertyNames.some((property) => property.startsWith('cta'));
    },
    displayLogoProperties() {
      return this.propertyNames.some((property) => property.startsWith('logo'));
    },
    displayLogoPlacement() {
      return this.propertyNames.some((property) => property.startsWith('logoPlacement'));
    },
    displayLogoSpacingProperties() {
      return this.propertyNames.some((property) => property.startsWith('logoMargin'));
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.FETCH_EXISTING_CREATIVES,
      contentTypes.PATCH_CREATIVE,
      contentTypes.PATCH_CREATIVE_PROPERTIES,
      contentTypes.TRIGGER_CREATIVE_RERENDER,
      contentTypes.SET_IS_SIDEBAR_INPUT_FOCUSED,
      contentTypes.RESET_CREATIVE_PROPERTIES,
    ]),
    updateProperty(propertyName, newValue) {
      this.updateProperties({
        [propertyName]: newValue,
      });
    },
    handleDelayedUpdates() {
      const allProperties = new Map();
      while (this.delayedUpdates.length > 0) {
        const nextUpdate = this.delayedUpdates.shift();
        for (const [key, value] of Object.entries(nextUpdate)) {
          allProperties.set(key, value);
        }
      }
      if (allProperties.size > 0) {
        this.patchProperties(Object.fromEntries(allProperties.entries()));
      }
    },
    async updateProperties(properties) {
      if (!this.isUpdateInProgress) {
        this.isUpdateInProgress = true;
        await this.patchProperties(properties);
        this.isUpdateInProgress = false;
        this.handleDelayedUpdates();
      } else {
        this.delayedUpdates.push(properties);
      }
    },
    async patchProperties(properties) {
      const payload = {
        advertisersId: this.selectedAdvertiser.id,
        creativeId: this.selectedCreativeId,
        properties,
      };
      await this.PATCH_CREATIVE_PROPERTIES(payload);
      this.TRIGGER_CREATIVE_RERENDER();
    },
    reset() {
      const payload = {
        advertiserId: this.campaign.advertiserId,
        campaignId: this.campaign.id,
        creativeId: this.selectedCreativeId,
      };
      this.RESET_CREATIVE_PROPERTIES(payload)
        .then(() => {
          this.TRIGGER_CREATIVE_RERENDER();
        });
    },
  },
};
</script>
<style lang="scss" scoped>
#margins-container {
  height: 88px;
}
span.disabled {
  cursor: not-allowed !important;
}
</style>
